import React from 'react';
import BackLink from './BackLink';
import clsx from 'clsx';

/**
 * PageHeader — canonical body-level page header (LAT-05)
 *
 * Anatomy:
 *   [back-link (optional, ABOVE title)]
 *   <h1>Title  {badge?}</h1>   [optional right-aligned action(s)]
 *   <p class="meta">…</p>       (`meta` PR-18 pattern; also legacy `subtitle`)
 *
 * PR-18 (2026-07-22): added inline `badge` slot (rendered immediately after the
 * title on the SAME LINE — never wraps below on narrow rows; the actions wrap
 * instead) and a `meta` slot for the single muted line beneath the title
 * (e.g. "Not sent · Last edited 2 days ago by Dana R."). Both are optional and
 * additive; existing callers using only `title` / `subtitle` / `actions` are
 * unchanged.
 *
 * Notes
 * - The AppBar (top of viewport) shows the COMPANY NAME, never the page name.
 *   Page name lives ONLY in this single H1 in the body.
 * - Detail pages must pass `backTo` so the link sits ABOVE the title.
 * - `actions` should typically be a single primary `<Button>` plus, at most,
 *   an Export ▾ dropdown. Keep this simple — no toolbars here.
 */
export default function PageHeader({
  title,
  subtitle,
  meta,       // PR-18: single muted line under title (alternative to subtitle)
  badge,      // PR-18: inline pill/badge rendered right after the title
  backTo,
  backState,  // IDI-157: router state for the Back link (e.g. reopen the origin tab)
  backLabel = 'Back',
  actions,
  className,
}) {
  return (
    <header data-portica-role="page-header" className={clsx('mb-6', className)}>
      {backTo && (
        <div className="mb-2">
          <BackLink to={backTo} state={backState} label={backLabel} />
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* text-h1: heading scale. --text-h1 is a `font` *shorthand*
              (600 32px/1.15 <family>), so it MUST be applied to the `font`
              property — assigning it to fontSize is invalid CSS and silently
              falls back to the inherited 14px body size (SYS-H1-01 regression).

              When `title` is a React element (e.g. a custom dropdown switcher),
              we drop `truncate` so its own popover / listbox children are not
              clipped by `overflow:hidden`. Plain-string titles keep truncation. */}
          {/* PR-18 (2026-07-22): title + inline badge on the SAME line.
              flex-nowrap + min-w-0 on the H1 wrapper keeps the badge stuck to
              the title — if the row is too narrow, the (right-column) actions
              wrap below instead. Badge NEVER wraps under the title. */}
          {(() => {
            const isElementTitle = React.isValidElement(title);
            return (
              <div className="flex items-center gap-2 flex-nowrap min-w-0">
                <h1
                  className={isElementTitle ? 'text-h1' : 'truncate text-h1'}
                  style={{
                    font: 'var(--text-h1)',
                    color: 'var(--color-text-default)',
                    letterSpacing: '-0.01em',
                    ...(isElementTitle ? { overflow: 'visible' } : null),
                  }}
                >
                  {title}
                </h1>
                {badge && (
                  <span className="flex-shrink-0" data-portica-role="page-header-badge">
                    {badge}
                  </span>
                )}
              </div>
            );
          })()}
          {/* PR-18: `meta` = single muted line beneath the title. `subtitle`
              retained for backward-compat (renders the same way). Preferred
              new callsites use `meta`. */}
          {(meta || subtitle) && (
            <p
              className="mt-1"
              style={{
                fontSize: 'var(--text-body)',
                color: 'var(--color-text-muted)',
              }}
            >
              {meta || subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
        )}
      </div>
    </header>
  );
}
